// features/schedule/components/detail/rawPhotoPermissions.ts
//
// Who may edit the raw-photo link list. Assigned photographers and active
// claimants own it while shooting. Afterwards, the assigned photographer may
// add the first handoff link; correcting an existing list requires a photo
// leader. Mirrors the BE gate on PUT /ekip/orders/:id/work-info.
//
// Self-claim (spec 16/08/2026): raising a hand puts the first claimant on the
// order, so they arrive here as the assigned photographer. While a slot is
// contested the other claimants are not on the order yet still work the shoot,
// so pass hasActiveClaim from useMyClaimedOrderIds().

import type { Order } from "@/features/orders/api";
import { opsRole } from "@/lib/auth/page-access";

/** Merge the JSONB array and the legacy single-link column, de-duplicated. */
export function extractRawPhotoLinks(order: {
  rawPhotoLink?: string | null;
  rawPhotoLinks?: unknown;
}): string[] {
  const links: string[] = [];
  if (Array.isArray(order.rawPhotoLinks)) {
    for (const value of order.rawPhotoLinks) {
      if (typeof value === "string" && value.trim()) links.push(value.trim());
    }
  }
  if (order.rawPhotoLink?.trim()) links.unshift(order.rawPhotoLink.trim());
  return Array.from(new Set(links));
}

/**
 * Đơn này lưu ảnh trong kho hay còn dán link?
 *
 * Đọc CỘT đã lưu, không suy từ mảng link: mảng link lật được (snapshot FE cũ
 * từng ghi đè về `[]`, trình nhập Appwrite ghi vào bất cứ lúc nào), mà phân
 * loại đơn thì không được lật. Cùng vị từ với `order.UsesAlbum` phía máy chủ.
 */
export function orderUsesAlbum(order: { photoSource?: string | null }): boolean {
  return orderPhotoStore(order) !== null;
}

/**
 * Ảnh của đơn nằm ở ĐÂU — để câu chữ chỉ đúng chỗ (QĐ 0243, CEO chốt 1a):
 * - `"album"`: Album của đơn (`photo_source='gallery'`, QĐ 0238) — năm thư mục,
 *   khách chọn qua link album;
 * - `"session"`: khối "Ảnh gốc" trong đơn (`photo_source='album'`, order_photos);
 * - `null`: link dán tay.
 * Hai giá trị đầu cùng LUẬT (không nhận link dán), khác CHỖ ảnh nằm.
 */
export function orderPhotoStore(order: {
  photoSource?: string | null;
}): "album" | "session" | null {
  const source = (order.photoSource ?? "").trim();
  if (source === "gallery") return "album";
  if (source === "album") return "session";
  return null;
}

export type LinkUser =
  | {
      id?: string | null;
      role?: string | null;
      department?: string | null;
      /** Chức danh HRM (QĐ 0425): `truong_ca` sửa link sau khoá như Trưởng cửa hàng. */
      jobTitleCode?: string | null;
    }
  | null
  | undefined;

/**
 * Các ô của đơn mà hai cổng dưới đây ĐỌC — không đòi trọn `Order`, để widget
 * "Lịch ê-kíp" (kiểu `EkipShift`, không phải `Order`) dùng CHUNG một luật thay
 * vì suy lại lần nữa: hai nơi cùng một luật mà suy riêng là chỗ để chúng trôi
 * khỏi nhau.
 *
 * `status` là `string` trần, CỐ Ý không lấy `Order["status"]`: union đó KHÔNG
 * có `tentative`, mà `STATUS_BEFORE_SHOOT` dưới đây thì có — thu về union của `Order`
 * là chặn đúng một trạng thái mà luật này đang xử lý.
 *
 * `photographers` thì ngược lại, neo vào hợp đồng: `isAssignedPhoto` so từng
 * `member.id`, nên hình dạng phải khớp `EkipMember` thật. Cả `Order` lẫn
 * `EkipShift` đều dùng chung schema đó.
 *
 * `EkipShift` KHÔNG mang `photoSource`, nên ở widget `orderUsesAlbum` trả
 * false — đúng hành vi cũ tại đó. Đơn dùng kho ảnh không lọt nhờ thế: máy chủ
 * chặn bằng `order.AlbumLockMessage`.
 */
export type RawPhotoGateOrder = {
  status?: string | null;
  photoSource?: string | null;
  shootDate?: string | null;
  photographers?: NonNullable<Order["photographers"]>;
  rawPhotoLink?: string | null;
  rawPhotoLinks?: unknown;
};

/**
 * Ngày chụp của đơn đã tới chưa (tính cả hôm nay).
 *
 * Không có ngày chụp → false: không có ngày thì không có căn cứ nào nói buổi
 * đã diễn ra. So bằng chuỗi `YYYY-MM-DD` nên không dính lệch múi giờ — cả hai
 * vế đều là ngày lịch, không phải mốc thời gian.
 */
export function shootDayReached(order: { shootDate?: string | null }, today = todayISO()): boolean {
  const day = (order.shootDate ?? "").slice(0, 10);
  if (!day) return false;
  return day <= today;
}

/** Hôm nay theo đồng hồ máy người dùng — ai mở màn này cũng đang ở VN. */
function todayISO(): string {
  const now = new Date();
  const m = `${now.getMonth() + 1}`.padStart(2, "0");
  const d = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

export function canEditRawPhotoLinks(
  user: LinkUser,
  order: RawPhotoGateOrder,
  hasActiveClaim = false,
  today = todayISO(),
): boolean {
  // Đơn album không nhận link nữa — máy chủ trả 400. Giấu hẳn ô nhập thay vì
  // để nó xám mờ hay để người ta gõ vào rồi ăn lỗi: chỗ nhập ảnh của đơn này
  // là khu tải ảnh ngay bên trên, và một ô vô dụng chỉ khiến người dùng tưởng
  // hệ thống hỏng.
  if (orderUsesAlbum(order)) return false;
  if (order.status === "cancelled") return false;
  // Đơn còn "đã đặt"/"hợp đồng": mốc mở ô là NGÀY CHỤP, không phải nút chuyển
  // trạng thái (CEO chốt 25/09/2026, phương án 3a). Ê-kíp nhận ca từ hôm trước
  // vẫn để đơn nằm ở "đã đặt" cho tới khi lễ tân bấm "Khách đã tới", nên buộc
  // chờ trạng thái là buộc người vừa chụp xong đi tìm người bấm hộ một cái
  // nút. Gương của máy chủ: ekipschedule.rawPhotoLinksForbidden.
  if (["booked", "contract"].includes(order.status ?? "")) {
    if (!shootDayReached(order, today)) return false;
    if (canCorrectLockedRawPhotoLinks(user)) return true;
    return (hasActiveClaim && isPhotoDepartment(user)) || isAssignedPhoto(user?.id, order);
  }
  if (canCorrectLockedRawPhotoLinks(user)) return true;
  if (order.status === "shooting") {
    return (hasActiveClaim && isPhotoDepartment(user)) || isAssignedPhoto(user?.id, order);
  }
  if (!isAssignedPhoto(user?.id, order)) return false;
  return extractRawPhotoLinks(order).length === 0;
}

/**
 * Trạng thái đơn CHƯA tới lượt chụp — ô đóng vì ca chưa mở, không phải vì ca
 * đã đi qua. Hai cảnh NGƯỢC nhau, nên câu giải thích phải khác nhau.
 *
 * Export để hộp Công việc của widget "Lịch ê-kíp" dùng đúng tập này cho ô ghi
 * chú và ô thẻ; bản sao thứ ba là chỗ để chúng trôi khỏi nhau.
 */
export const STATUS_BEFORE_SHOOT: ReadonlySet<string> = new Set([
  "tentative",
  "giữ chỗ tạm",
  "booked",
  "contract",
]);

/**
 * Câu giải thích vì sao ô link ảnh gốc đang đóng với người đang xem.
 * `null` = không cần nói gì.
 *
 * Trước 25/09/2026 hai màn (Lịch chụp, Đơn hàng) chỉ nhận một cờ `locked =
 * status !== "shooting"` rồi in CHUNG một câu "Ca đã chuyển trạng thái, cần
 * Leader Photo chỉnh sửa link ảnh gốc". Cờ đó gộp hai tình huống NGƯỢC NHAU:
 * đơn chưa tới lượt chụp và đơn đã chụp xong. Người kiểm thử 25/09/2026 mở một
 * đơn còn "Đã đặt" và đọc được câu "Ca đã chuyển trạng thái" — đúng lúc đơn
 * chưa chuyển gì cả — nên báo là hệ thống hỏng.
 *
 * `user` là tuỳ chọn và mặc định là "không biết người xem". Truyền vào thì câu
 * nói phân biệt được hai cảnh mà nếu chỉ nhìn trạng thái đơn sẽ gộp làm một:
 * ca chưa tới ngày, và ca đã tới ngày nhưng người đang xem không thuộc ê-kíp.
 *
 * Đơn dùng kho ảnh trả `null`: cửa dán link đóng vì ảnh nằm ở khu tải ảnh, nói
 * chuyện trạng thái ca ở đó là lạc đề.
 */
export function rawPhotoLockNotice(
  order: RawPhotoGateOrder,
  user: LinkUser = null,
  hasActiveClaim = false,
  today = todayISO(),
): string | null {
  if (orderUsesAlbum(order)) return null;
  const status = (order.status ?? "").trim();
  if (status === "shooting") return null;
  if (status === "cancelled") return "Đơn đã hủy — không nhận link ảnh gốc nữa.";
  // Ô đang mở với chính người này thì không có gì để giải thích. Hỏi đúng hàm
  // quyền thay vì đoán lại từ trạng thái — hai nơi cùng một luật mà suy riêng
  // là chỗ để chúng trôi khỏi nhau.
  if (canEditRawPhotoLinks(user, order, hasActiveClaim, today)) return null;
  if (STATUS_BEFORE_SHOOT.has(status)) {
    // Mốc mở ô là NGÀY CHỤP chứ không phải nút chuyển trạng thái (CEO chốt
    // 25/09/2026, phương án 3a) — nên câu cũ "mở ca xong mới dán link được"
    // nay sai: tới ngày là dán được, không ai phải bấm gì.
    if (!shootDayReached(order, today)) {
      return "Chưa tới ngày chụp — dán link ảnh gốc sau khi chụp xong.";
    }
    return "Chỉ thợ chụp của ca này dán được link ảnh gốc.";
  }
  return "Ca đã chuyển trạng thái, cần Leader Photo chỉnh sửa link ảnh gốc.";
}

function isPhotoDepartment(user: LinkUser): boolean {
  return (user?.department ?? "").toLowerCase().startsWith("photo");
}

// Gương BE order.canCorrectLockedRawPhotoLinks: TCH mọi phòng; leader/QC chỉ
// khối ảnh (spec 2026-08-23 §5.7). Vai Điều phối khoá 23/08/2026 (quyết định
// 0050). Thân hàm này soi gương `canCorrectLockedEkipInfo` ở
// features/ekip-schedule/lockPermissions.ts — sửa một bên phải sửa cả hai.
function canCorrectLockedRawPhotoLinks(user: LinkUser): boolean {
  // Trưởng ca như Trưởng cửa hàng (QĐ 0425) — gương `PatchInput.ActorOpsRole`.
  const role = opsRole(user).toLowerCase();
  const dept = (user?.department ?? "").toLowerCase();
  return (
    role === "admin" ||
    role === "director" ||
    role === "branch_manager" ||
    ((role === "manager" || role === "qc") && dept.startsWith("photo"))
  );
}

function isAssignedPhoto(userId: string | null | undefined, order: RawPhotoGateOrder): boolean {
  if (!userId) return false;
  if (!Array.isArray(order.photographers)) return false;
  return order.photographers.some(
    (member) =>
      typeof member === "object" && member !== null && "id" in member && member.id === userId,
  );
}
